import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Avatar,
  Box,
  Button,
  TextField,
  Tabs,
  Tab,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  IconButton,
  Chip,
  Paper,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  Menu,
  MenuItem,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert
} from '@mui/material';
import ThumbUp from '@mui/icons-material/ThumbUp';
import ThumbDown from '@mui/icons-material/ThumbDown';
import CommentIcon from '@mui/icons-material/Comment';
import Share from '@mui/icons-material/Share';
import MoreVert from '@mui/icons-material/MoreVert';
import Send from '@mui/icons-material/Send';
import Search from '@mui/icons-material/Search';
import FilterList from '@mui/icons-material/FilterList';
import Bookmark from '@mui/icons-material/Bookmark';
import BookmarkBorder from '@mui/icons-material/BookmarkBorder';
import SmartToy from '@mui/icons-material/SmartToy';
import FlagOutlined from '@mui/icons-material/FlagOutlined';
import Refresh from '@mui/icons-material/Refresh';
import Verified from '@mui/icons-material/Verified';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import { useAuth } from '../context/AuthContext';
import { useCommunity, Discussion, type Comment, type DiscussionMedia } from '../context/CommunityContext';
import VisitorMetrics from '../components/analytics/VisitorMetrics';
import { supabase } from '../lib/supabase';
import { useUserFarmSelection } from '../hooks/useUserFarmSelection';

const normalizeAssistantText = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const ASSISTANT_STOP_WORDS = new Set([
  'about', 'after', 'again', 'also', 'among', 'been', 'being', 'best', 'between', 'both', 'bring', 'came',
  'can', 'could', 'does', 'doing', 'farm', 'farming', 'from', 'have', 'help', 'into', 'just', 'make', 'more',
  'need', 'over', 'same', 'should', 'some', 'than', 'that', 'them', 'there', 'they', 'this', 'those', 'through',
  'under', 'using', 'very', 'want', 'what', 'when', 'where', 'which', 'with', 'would', 'your',
]);

const tokenizeAssistantText = (value: string) =>
  normalizeAssistantText(value)
    .split(' ')
    .filter((term) => term.length > 2 && !ASSISTANT_STOP_WORDS.has(term));

const buildCommunityAssistantResponse = (
  query: string,
  discussions: Discussion[],
  contributorById: Map<string, ContributorSummary>,
  expertByUserId: Map<string, ExpertProfile>,
  approvedExperts: ExpertProfile[],
) => {
  const visibleDiscussions = discussions.filter((discussion) => discussion.moderationStatus !== 'hidden' && discussion.moderationStatus !== 'removed');
  const normalizedQuery = normalizeAssistantText(query);
  const queryTerms = Array.from(new Set(tokenizeAssistantText(query)));

  if (!queryTerms.length) {
    return 'Ask a more specific farming question and I will search the visible community discussions, tags, counties, and verified experts for relevant guidance.';
  }

  const rankedMatches = visibleDiscussions
    .map((discussion) => {
      const titleTerms = tokenizeAssistantText(discussion.title);
      const contentTerms = tokenizeAssistantText(discussion.content);
      const locationTerms = tokenizeAssistantText(discussion.author.location);
      const normalizedTags = (discussion.tags || []).map((tag) => normalizeAssistantText(tag));
      const contributor = contributorById.get(discussion.author.id);
      const expertProfile = expertByUserId.get(discussion.author.id);
      const badge = getContributorBadge(discussion, contributor, expertProfile);
      const matchReasons: string[] = [];
      let score = 0;

      queryTerms.forEach((term) => {
        if (titleTerms.includes(term)) {
          score += 6;
          matchReasons.push('title match');
        }

        if (normalizedTags.some((tag) => tag.includes(term))) {
          score += 5;
          matchReasons.push('tag match');
        }

        if (locationTerms.includes(term)) {
          score += 4;
          matchReasons.push('county/location match');
        }

        if (contentTerms.includes(term)) {
          score += 2;
          matchReasons.push('content match');
        }
      });

      if (normalizeAssistantText(discussion.title).includes(normalizedQuery)) {
        score += 6;
        matchReasons.push('strong title phrase match');
      }

      if (normalizeAssistantText(discussion.content).includes(normalizedQuery)) {
        score += 4;
        matchReasons.push('strong content phrase match');
      }

      if (expertProfile?.verificationStatus === 'approved') {
        score += 4;
        matchReasons.push('verified expert source');
      } else if ((discussion.author.role || '').toLowerCase() === 'admin') {
        score += 3;
        matchReasons.push('platform admin source');
      } else if (badge.label === 'Top Contributor') {
        score += 2;
        matchReasons.push('trusted contributor source');
      }

      score += Math.min(discussion.comments, 6) * 0.7;
      score += Math.min(discussion.likes, 8) * 0.25;
      score += Math.min(discussion.views, 200) / 100;

      if (discussion.comments === 0) {
        score -= 0.5;
      }

      return {
        discussion,
        score,
        badge,
        expertProfile,
        matchReasons: Array.from(new Set(matchReasons)),
      };
    })
    .filter((entry) => entry.score > 0)
    .sort(
      (a, b) =>
        b.score - a.score ||
        b.discussion.comments - a.discussion.comments ||
        b.discussion.likes - a.discussion.likes ||
        new Date(b.discussion.date).getTime() - new Date(a.discussion.date).getTime(),
    )
    .slice(0, 3);

  const matchingExperts = approvedExperts
    .filter((expert) => {
      const expertHaystack = normalizeAssistantText(
        `${expert.name} ${expert.location} ${expert.headline || ''} ${expert.bio || ''} ${expert.expertiseTags.join(' ')} ${expert.counties.join(' ')}`,
      );
      return queryTerms.some((term) => expertHaystack.includes(term));
    })
    .sort((a, b) => (b.expertiseTags.length - a.expertiseTags.length) || a.name.localeCompare(b.name))
    .slice(0, 2);

  if (!rankedMatches.length) {
    const trendingTags = Array.from(
      new Set(
        visibleDiscussions
          .flatMap((discussion) => discussion.tags || [])
          .map((tag) => tag.trim())
          .filter(Boolean),
      ),
    ).slice(0, 5);

    const expertLead = matchingExperts.length
      ? `You may also want input from ${matchingExperts.map((expert) => `${expert.name} (${expert.headline || expert.location})`).join(', ')}.`
      : 'No verified expert profile currently matches those terms closely.';

    return [
      `I could not find a strong community discussion match for "${query}".`,
      visibleDiscussions.length
        ? `Try searching or posting with terms like ${trendingTags.length ? trendingTags.join(', ') : 'crop name, disease, market, irrigation, pest'}.`
        : 'The community does not have enough published discussions yet. Start a post with your crop, county, challenge, and timing to get targeted responses.',
      expertLead,
      'For the best results, mention the crop, county, season, input used, and the exact problem you are facing.',
    ].join('\n\n');
  }

  const topScore = rankedMatches[0]?.score || 0;
  const confidence = topScore >= 16 ? 'high' : topScore >= 10 ? 'medium' : 'emerging';
  const relatedTags = Array.from(
    new Set(rankedMatches.flatMap(({ discussion }) => discussion.tags || []).map((tag) => tag.trim()).filter(Boolean)),
  ).slice(0, 6);

  const summaries = rankedMatches.map(({ discussion, badge, matchReasons }, index) => {
    const excerpt = discussion.content.length > 180
      ? `${discussion.content.slice(0, 177).trim()}...`
      : discussion.content;
    const tags = discussion.tags?.length ? ` Tags: ${discussion.tags.join(', ')}.` : '';
    const reasons = matchReasons.slice(0, 3).join(', ');
    return `${index + 1}. ${discussion.title}\nSource: ${discussion.author.name} (${badge.label}, ${discussion.author.location})\nWhy it matched: ${reasons || 'general relevance'}.\nEvidence: ${discussion.comments} comments, ${discussion.likes} likes, ${discussion.views} views.\n${excerpt}${tags}`;
  });

  const expertsSection = matchingExperts.length
    ? `Experts to consult: ${matchingExperts.map((expert) => `${expert.name} (${expert.headline || expert.location})`).join('; ')}.`
    : 'Experts to consult: No verified expert profile closely matches this question yet, so a new post may attract a fresh response.';

  return [
    `Community knowledge summary for "${query}"`,
    `Confidence: ${confidence}. I found ${rankedMatches.length} visible discussion${rankedMatches.length > 1 ? 's' : ''} ranked by title/tag relevance, county fit, contributor trust, and community engagement.`,
    `Best matches:\n${summaries.join('\n\n')}`,
    expertsSection,
    relatedTags.length ? `Related community tags: ${relatedTags.join(', ')}.` : 'Related community tags are still limited for this topic.',
    'Recommended next step: review the top match first, then ask a follow-up question with your county, crop stage, and current inputs if you still need farm-specific advice.',
  ].join('\n\n');
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`community-tabpanel-${index}`}
      aria-labelledby={`community-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

type ContributorSummary = {
  id: string;
  name: string;
  avatar: string;
  location: string;
  role?: string | null;
  posts: number;
  likes: number;
  comments: number;
  engagement: number;
};

type ExpertProfile = {
  userId: string;
  name: string;
  avatar: string;
  location: string;
  headline: string | null;
  bio: string | null;
  expertiseTags: string[];
  counties: string[];
  verificationStatus: 'pending' | 'approved' | 'rejected';
  requestedAt: string;
  reviewedAt: string | null;
  rejectionReason: string | null;
};

type FarmerVerification = {
  userId: string;
  verificationStatus: 'pending' | 'approved' | 'rejected';
  requestNotes: string | null;
  farmCountSnapshot: number;
  requestedAt: string;
  reviewedAt: string | null;
  rejectionReason: string | null;
};

type CommunityReport = {
  id: string;
  targetType: 'discussion' | 'comment';
  discussionId: string | null;
  commentId: string | null;
  reason: string;
  details: string | null;
  status: 'open' | 'reviewed' | 'resolved' | 'dismissed';
  createdAt: string;
  reviewedAt: string | null;
  targetTitle: string;
  reporterName: string;
  targetModerationStatus: 'visible' | 'hidden' | 'removed';
  resolutionNotes: string | null;
  reviewedByName: string | null;
};

type AdminReviewAction = 'reviewed' | 'resolved' | 'dismissed' | 'hidden' | 'removed';
type ModerationPriority = 'critical' | 'high' | 'medium' | 'low';

type ModerationQueueItem = CommunityReport & {
  targetKey: string;
  relatedReportCount: number;
  uniqueReporterCount: number;
  priority: ModerationPriority;
  priorityScore: number;
  escalationReasons: string[];
};

type ReportTarget =
  | { type: 'discussion'; id: string; title: string }
  | { type: 'comment'; id: string; title: string };

const isTableMissingError = (error: any) =>
  ['42P01', '42703'].includes(error?.code || '');

const REPORT_REASON_LABELS: Record<string, string> = {
  misinformation: 'Misinformation',
  unsafe_advice: 'Unsafe Advice',
  spam: 'Spam',
  abuse: 'Abuse',
  harassment: 'Harassment',
  other: 'Other',
};

const PRIORITY_SORT_ORDER: Record<ModerationPriority, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

const getReportReasonLabel = (reason: string) => REPORT_REASON_LABELS[reason] || reason;

const getContributorBadge = (
  discussion: Discussion,
  contributor?: ContributorSummary,
  expertProfile?: ExpertProfile,
) => {
  const role = (discussion.author.role || '').toLowerCase();

  if (role === 'admin') {
    return { label: 'Platform Admin', color: 'error' as const };
  }

  if (expertProfile?.verificationStatus === 'approved') {
    return { label: 'Verified Expert', color: 'secondary' as const };
  }

  if (['logistics_company', 'transporter', 'vehicle_owner'].includes(role)) {
    return { label: 'Verified Partner', color: 'info' as const };
  }

  if ((contributor?.posts || 0) >= 2 && (contributor?.engagement || 0) >= 5) {
    return { label: 'Top Contributor', color: 'success' as const };
  }

  if ((contributor?.posts || 0) > 0) {
    return { label: 'Community Member', color: 'default' as const };
  }

  return { label: 'New Member', color: 'default' as const };
};

const CommunityHub: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const {
    discussions,
    savedPosts,
    addDiscussion,
    updateDiscussion,
    deleteDiscussion,
    toggleSavePost,
    toggleLikePost,
    getComments,
    addComment,
    updateComment,
    deleteComment,
    refreshDiscussions,
    loading: communityLoading,
  } = useCommunity();
  const { farms, selectedFarmId } = useUserFarmSelection();
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [sortOption, setSortOption] = useState('recent');
  const [filterMode, setFilterMode] = useState<'all' | 'unanswered'>('all');
  const [aiQuery, setAiQuery] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [newPostTitle, setNewPostTitle] = useState('');
  const [newPostContent, setNewPostContent] = useState('');
  const [newPostTags, setNewPostTags] = useState('');
  const [composerPostType, setComposerPostType] = useState<'text' | 'farm_update' | 'photo' | 'video' | 'photo_carousel'>('text');
  const [composerFarmId, setComposerFarmId] = useState<string>('');
  const [composerMediaFiles, setComposerMediaFiles] = useState<File[]>([]);
  const [composerMediaPreviews, setComposerMediaPreviews] = useState<{ url: string; kind: 'image' | 'video' }[]>([]);
  const [composerMedia, setComposerMedia] = useState<DiscussionMedia[]>([]);
  const [composerMediaUploading, setComposerMediaUploading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedDiscussion, setSelectedDiscussion] = useState<Discussion | null>(null);
  const [workflowWarnings, setWorkflowWarnings] = useState<string[]>([]);
  const [approvedExperts, setApprovedExperts] = useState<ExpertProfile[]>([]);
  const [myExpertProfile, setMyExpertProfile] = useState<ExpertProfile | null>(null);
  const [pendingExperts, setPendingExperts] = useState<ExpertProfile[]>([]);
  const [verifiedFarmerByUserId, setVerifiedFarmerByUserId] = useState<Record<string, boolean>>({});
  const [myFarmerVerification, setMyFarmerVerification] = useState<FarmerVerification | null>(null);
  const [pendingFarmerVerifications, setPendingFarmerVerifications] = useState<FarmerVerification[]>([]);
  const [farmerDialogOpen, setFarmerDialogOpen] = useState(false);
  const [farmerRequestNotes, setFarmerRequestNotes] = useState('');
  const [farmerSubmitting, setFarmerSubmitting] = useState(false);
  const [communityReports, setCommunityReports] = useState<CommunityReport[]>([]);
  const [moderationHistory, setModerationHistory] = useState<CommunityReport[]>([]);
  const [loadingWorkflows, setLoadingWorkflows] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportTarget, setReportTarget] = useState<ReportTarget | null>(null);
  const [reportReason, setReportReason] = useState('misinformation');
  const [reportDetails, setReportDetails] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [expertDialogOpen, setExpertDialogOpen] = useState(false);
  const [expertHeadline, setExpertHeadline] = useState('');
  const [expertBio, setExpertBio] = useState('');
  const [expertTags, setExpertTags] = useState('');
  const [expertCounties, setExpertCounties] = useState('');
  const [expertSubmitting, setExpertSubmitting] = useState(false);
  const [adminReviewDialogOpen, setAdminReviewDialogOpen] = useState(false);
  const [adminReviewTarget, setAdminReviewTarget] = useState<CommunityReport | null>(null);
  const [adminReviewAction, setAdminReviewAction] = useState<AdminReviewAction>('reviewed');
  const [adminReviewNotes, setAdminReviewNotes] = useState('');
  const [adminReviewSubmitting, setAdminReviewSubmitting] = useState(false);
  const [composerMode, setComposerMode] = useState<'create' | 'edit'>('create');
  const [submittingDiscussion, setSubmittingDiscussion] = useState(false);
  const [editingDiscussionId, setEditingDiscussionId] = useState<string | null>(null);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentContent, setEditingCommentContent] = useState('');
  const [savingComment, setSavingComment] = useState(false);
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(null);
  const [deletingDiscussionId, setDeletingDiscussionId] = useState<string | null>(null);
  const isAdmin = user?.role === 'admin';

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleShare = (discussionTitle: string) => {
    navigator.clipboard.writeText(`Check out this discussion: ${discussionTitle}`);
    setSnackbarMessage('Link copied to clipboard!');
    setSnackbarOpen(true);
  };

  const handleViewPost = (discussion: any) => {
    setSelectedDiscussion(discussion);
    setEditingCommentId(null);
    setEditingCommentContent('');
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setEditingCommentId(null);
    setEditingCommentContent('');
    setNewComment('');
  };

  useEffect(() => {
    if (!composerFarmId && selectedFarmId) {
      setComposerFarmId(selectedFarmId);
    }
  }, [composerFarmId, selectedFarmId]);

  const clearComposerMedia = () => {
    composerMediaPreviews.forEach((preview) => {
      if (preview.url.startsWith('blob:')) {
        URL.revokeObjectURL(preview.url);
      }
    });
    setComposerMediaFiles([]);
    setComposerMediaPreviews([]);
    setComposerMedia([]);
  };

  const handleComposerMediaChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextFiles = Array.from(event.target.files || []);
    clearComposerMedia();

    if (!nextFiles.length) {
      return;
    }

    setComposerMediaFiles(nextFiles);
    setComposerMediaPreviews(
      nextFiles.map((file) => ({
        url: URL.createObjectURL(file),
        kind: file.type.startsWith('video/') ? 'video' : 'image',
      })),
    );
  };

  const resetDiscussionComposer = () => {
    setComposerMode('create');
    setEditingDiscussionId(null);
    setNewPostTitle('');
    setNewPostContent('');
    setNewPostTags('');
    setComposerPostType('text');
    setComposerFarmId(selectedFarmId || '');
    clearComposerMedia();
  };

  const startDiscussionEdit = (discussion: Discussion) => {
    setComposerMode('edit');
    setEditingDiscussionId(discussion.id);
    setNewPostTitle(discussion.title);
    setNewPostContent(discussion.content);
    setNewPostTags(discussion.tags.join(', '));
    setComposerPostType(discussion.postType || 'text');
    setComposerFarmId(discussion.farmId || selectedFarmId || '');
    clearComposerMedia();
    if (discussion.media?.length) {
      setComposerMedia(discussion.media);
      setComposerMediaPreviews(
        discussion.media.map((item) => ({ url: item.url, kind: item.mediaKind })),
      );
    }
    setTabValue(3);
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleCloseSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  useEffect(() => {
    if (selectedDiscussion && viewDialogOpen) {
      getComments(selectedDiscussion.id).then(setComments);
    }
  }, [selectedDiscussion, viewDialogOpen]);

  useEffect(() => {
    if (!selectedDiscussion) return;

    const refreshedDiscussion = discussions.find((discussion) => discussion.id === selectedDiscussion.id);
    if (refreshedDiscussion) {
      setSelectedDiscussion(refreshedDiscussion);
      return;
    }

    if (!communityLoading) {
      setSelectedDiscussion(null);
      setComments([]);
      setViewDialogOpen(false);
    }
  }, [communityLoading, discussions, selectedDiscussion]);

  const loadVerifiedFarmers = async (userIds: string[]) => {
    const uniqueIds = Array.from(new Set(userIds.filter(Boolean)));
    if (uniqueIds.length === 0) return;

    try {
      const { data, error } = await supabase
        .from('community_farmer_verifications')
        .select('user_id, verification_status')
        .in('user_id', uniqueIds)
        .eq('verification_status', 'approved');

      if (error) {
        if (isTableMissingError(error)) return;
        throw error;
      }

      setVerifiedFarmerByUserId((prev) => {
        const next = { ...prev };
        (data || []).forEach((row: any) => {
          if (row?.user_id) next[String(row.user_id)] = true;
        });
        return next;
      });
    } catch (error) {
      return;
    }
  };

  useEffect(() => {
    void loadVerifiedFarmers(discussions.map((discussion) => discussion.author.id));
  }, [discussions]);

  useEffect(() => {
    void loadVerifiedFarmers(comments.map((comment) => comment.author.id));
  }, [comments]);

  const loadCommunityWorkflows = async () => {
    setLoadingWorkflows(true);
    const nextWarnings: string[] = [];

    try {
      const approvedExpertsRequest = supabase
        .from('community_expert_profiles')
        .select(`
          user_id,
          headline,
          bio,
          expertise_tags,
          counties,
          verification_status,
          requested_at,
          reviewed_at,
          rejection_reason,
          profile:profiles!community_expert_profiles_user_id_fkey(id, name, county)
        `)
        .eq('verification_status', 'approved')
        .order('reviewed_at', { ascending: false });

      const myExpertRequest = user
        ? supabase
            .from('community_expert_profiles')
            .select(`
              user_id,
              headline,
              bio,
              expertise_tags,
              counties,
              verification_status,
              requested_at,
              reviewed_at,
              rejection_reason,
              profile:profiles!community_expert_profiles_user_id_fkey(id, name, county)
            `)
            .eq('user_id', user.id)
            .maybeSingle()
        : Promise.resolve({ data: null, error: null } as any);

      const adminExpertRequest = isAdmin
        ? supabase
            .from('community_expert_profiles')
            .select(`
              user_id,
              headline,
              bio,
              expertise_tags,
              counties,
              verification_status,
              requested_at,
              reviewed_at,
              rejection_reason,
              profile:profiles!community_expert_profiles_user_id_fkey(id, name, county)
            `)
            .eq('verification_status', 'pending')
            .order('requested_at', { ascending: true })
        : Promise.resolve({ data: [], error: null } as any);

      const myFarmerRequest = user
        ? supabase
            .from('community_farmer_verifications')
            .select('user_id, verification_status, request_notes, farm_count_snapshot, requested_at, reviewed_at, rejection_reason')
            .eq('user_id', user.id)
            .maybeSingle()
        : Promise.resolve({ data: null, error: null } as any);

      const adminFarmerRequest = isAdmin
        ? supabase
            .from('community_farmer_verifications')
            .select('user_id, verification_status, request_notes, farm_count_snapshot, requested_at, reviewed_at, rejection_reason')
            .eq('verification_status', 'pending')
            .order('requested_at', { ascending: true })
            .limit(50)
        : Promise.resolve({ data: [], error: null } as any);

      const adminReportsRequest = isAdmin
        ? supabase
            .from('community_reports')
            .select(`
              id,
              target_type,
              discussion_id,
              comment_id,
              reason,
              details,
              status,
              created_at,
              reviewed_at,
              resolution_notes,
              reporter:profiles!community_reports_reporter_id_fkey(name),
              reviewer:profiles!community_reports_reviewed_by_fkey(name),
              discussion:discussions(title, moderation_status),
              comment:discussion_comments(content, moderation_status)
            `)
            .in('status', ['open', 'reviewed'])
            .order('created_at', { ascending: true })
            .limit(50)
        : Promise.resolve({ data: [], error: null } as any);

      const adminHistoryReportsRequest = isAdmin
        ? supabase
            .from('community_reports')
            .select(`
              id,
              target_type,
              discussion_id,
              comment_id,
              reason,
              details,
              status,
              created_at,
              reviewed_at,
              resolution_notes,
              reporter:profiles!community_reports_reporter_id_fkey(name),
              reviewer:profiles!community_reports_reviewed_by_fkey(name),
              discussion:discussions(title, moderation_status),
              comment:discussion_comments(content, moderation_status)
            `)
            .in('status', ['resolved', 'dismissed'])
            .order('reviewed_at', { ascending: false })
            .limit(8)
        : Promise.resolve({ data: [], error: null } as any);

      const [approvedExpertsResult, myExpertResult, adminExpertsResult, myFarmerResult, adminFarmerResult, adminReportsResult, adminHistoryReportsResult] = await Promise.all([
        approvedExpertsRequest,
        myExpertRequest,
        adminExpertRequest,
        myFarmerRequest,
        adminFarmerRequest,
        adminReportsRequest,
        adminHistoryReportsRequest,
      ]);

      const mapExpertProfile = (row: any): ExpertProfile => ({
        userId: row.user_id,
        name: row.profile?.name || 'Unknown Expert',
        avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(row.profile?.name || 'Unknown Expert')}`,
        location: row.profile?.county || 'Kenya',
        headline: row.headline || null,
        bio: row.bio || null,
        expertiseTags: row.expertise_tags || [],
        counties: row.counties || [],
        verificationStatus: row.verification_status,
        requestedAt: row.requested_at,
        reviewedAt: row.reviewed_at || null,
        rejectionReason: row.rejection_reason || null,
      });

      const mapFarmerVerification = (row: any): FarmerVerification => ({
        userId: row.user_id,
        verificationStatus: row.verification_status,
        requestNotes: row.request_notes || null,
        farmCountSnapshot: Number(row.farm_count_snapshot || 0),
        requestedAt: row.requested_at,
        reviewedAt: row.reviewed_at || null,
        rejectionReason: row.rejection_reason || null,
      });

      if (approvedExpertsResult.error) {
        if (!isTableMissingError(approvedExpertsResult.error)) throw approvedExpertsResult.error;
        nextWarnings.push('Community expert verification tables are not deployed yet. Run the latest Supabase migration to enable verified experts.');
      } else {
        setApprovedExperts(((approvedExpertsResult.data as any[]) || []).map(mapExpertProfile));
      }

      if (myExpertResult.error) {
        if (!isTableMissingError(myExpertResult.error)) throw myExpertResult.error;
        if (!nextWarnings.some((warning) => warning.includes('verified experts'))) {
          nextWarnings.push('Community expert verification tables are not deployed yet. Run the latest Supabase migration to enable verified experts.');
        }
      } else {
        setMyExpertProfile(myExpertResult.data ? mapExpertProfile(myExpertResult.data) : null);
      }

      if (adminExpertsResult.error) {
        if (!isTableMissingError(adminExpertsResult.error)) throw adminExpertsResult.error;
        nextWarnings.push('Expert review queue is unavailable until the latest community migration is applied.');
      } else {
        setPendingExperts(((adminExpertsResult.data as any[]) || []).map(mapExpertProfile));
      }

      if (myFarmerResult.error) {
        if (!isTableMissingError(myFarmerResult.error)) throw myFarmerResult.error;
      } else {
        setMyFarmerVerification(myFarmerResult.data ? mapFarmerVerification(myFarmerResult.data) : null);
      }

      if (adminFarmerResult.error) {
        if (!isTableMissingError(adminFarmerResult.error)) throw adminFarmerResult.error;
      } else {
        setPendingFarmerVerifications(((adminFarmerResult.data as any[]) || []).map(mapFarmerVerification));
      }

      if (adminReportsResult.error) {
        if (!isTableMissingError(adminReportsResult.error)) throw adminReportsResult.error;
        nextWarnings.push('Moderation reports are unavailable until the latest community migration is applied.');
      } else {
        const mapReport = (row: any): CommunityReport => ({
            id: row.id,
            targetType: row.target_type,
            discussionId: row.discussion_id,
            commentId: row.comment_id,
            reason: row.reason,
            details: row.details || null,
            status: row.status,
            createdAt: row.created_at,
            reviewedAt: row.reviewed_at || null,
            targetTitle:
              row.target_type === 'discussion'
                ? row.discussion?.title || 'Reported discussion'
                : row.comment?.content?.slice(0, 80) || 'Reported comment',
            reporterName: row.reporter?.name || 'Unknown reporter',
            targetModerationStatus:
              row.target_type === 'discussion'
                ? row.discussion?.moderation_status || 'visible'
                : row.comment?.moderation_status || 'visible',
            resolutionNotes: row.resolution_notes || null,
            reviewedByName: row.reviewer?.name || null,
          });

        setCommunityReports(((adminReportsResult.data as any[]) || []).map(mapReport));

        if (adminHistoryReportsResult.error) {
          if (!isTableMissingError(adminHistoryReportsResult.error)) throw adminHistoryReportsResult.error;
        } else {
          setModerationHistory(((adminHistoryReportsResult.data as any[]) || []).map(mapReport));
        }
      }

      setWorkflowWarnings(nextWarnings);
    } catch (error: any) {
      setWorkflowWarnings([error?.message || 'Community workflow data could not be loaded.']);
    } finally {
      setLoadingWorkflows(false);
    }
  };

  useEffect(() => {
    void loadCommunityWorkflows();
  }, [user?.id, isAdmin]);

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedDiscussion) return;
    try {
      await addComment(selectedDiscussion.id, newComment);
      setNewComment('');
      const refreshedComments = await getComments(selectedDiscussion.id);
      setComments(refreshedComments);
      setSnackbarMessage('Comment added successfully');
      setSnackbarOpen(true);
    } catch (error: any) {
      console.error('Error adding comment:', error);
      setSnackbarMessage(error?.message || 'Failed to add comment.');
      setSnackbarOpen(true);
    }
  };

  const handleSaveDiscussion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPostTitle.trim() || !newPostContent.trim()) return;

    setSubmittingDiscussion(true);
    try {
      let resolvedMedia: DiscussionMedia[] = composerMedia;

      if (composerMediaFiles.length) {
        if (!user) {
          throw new Error('Sign in to upload community media.');
        }

        setComposerMediaUploading(true);
        try {
          const uploaded: DiscussionMedia[] = [];
          for (let index = 0; index < composerMediaFiles.length; index += 1) {
            const file = composerMediaFiles[index];
            const safeOriginalName = file.name.replace(/[^\w.\-]+/g, '_');
            const storagePath = `${user.id}/${Date.now()}_${index}_${safeOriginalName}`;
            const mediaKind: 'image' | 'video' = file.type.startsWith('video/') ? 'video' : 'image';

            const { error: uploadError } = await supabase.storage
              .from('community-media')
              .upload(storagePath, file, {
                upsert: false,
                contentType: file.type || 'application/octet-stream',
                cacheControl: '3600',
              });

            if (uploadError) {
              throw uploadError;
            }

            const { data } = await supabase.storage
              .from('community-media')
              .createSignedUrl(storagePath, 60 * 60);
            uploaded.push({
              id: storagePath,
              url: data?.signedUrl || '',
              storagePath,
              mediaKind,
              mimeType: file.type || null,
              caption: null,
              sortOrder: index,
            });
          }

          resolvedMedia = uploaded;
          setComposerMedia(resolvedMedia);
        } finally {
          setComposerMediaUploading(false);
        }
      }

      const inferredPostType: NonNullable<Discussion['postType']> =
        resolvedMedia.some((m) => m.mediaKind === 'video')
          ? 'video'
          : resolvedMedia.length > 1
            ? 'photo_carousel'
            : resolvedMedia.length === 1
              ? 'photo'
              : 'text';

      const resolvedPostType: NonNullable<Discussion['postType']> =
        composerPostType === 'text' ? inferredPostType : composerPostType;

      const payload: Omit<Discussion, 'id' | 'date' | 'likes' | 'comments' | 'views' | 'author' | 'isLiked'> = {
        title: newPostTitle.trim(),
        content: newPostContent.trim(),
        tags: newPostTags.split(',').map((tag) => tag.trim()).filter(Boolean),
        postType: resolvedPostType,
        farmId: composerFarmId || null,
        media: resolvedMedia,
      };

      if (composerMode === 'edit' && editingDiscussionId) {
        await updateDiscussion(editingDiscussionId, payload);
        setSnackbarMessage('Discussion updated successfully.');
      } else {
        await addDiscussion(payload);
        setSnackbarMessage('Discussion posted successfully.');
      }

      setSnackbarOpen(true);
      resetDiscussionComposer();
      setTabValue(0);
    } catch (error: any) {
      setSnackbarMessage(error?.message || 'Failed to save discussion.');
      setSnackbarOpen(true);
    } finally {
      setSubmittingDiscussion(false);
    }
  };

  const handleDeleteDiscussion = async (discussion: Discussion) => {
    if (!window.confirm(`Delete "${discussion.title}"? This will also remove its comments.`)) {
      return;
    }

    setDeletingDiscussionId(discussion.id);
    try {
      await deleteDiscussion(discussion.id);
      if (selectedDiscussion?.id === discussion.id) {
        handleCloseViewDialog();
        setSelectedDiscussion(null);
        setComments([]);
      }
      setSnackbarMessage('Discussion deleted successfully.');
      setSnackbarOpen(true);
    } catch (error: any) {
      setSnackbarMessage(error?.message || 'Failed to delete discussion.');
      setSnackbarOpen(true);
    } finally {
      setDeletingDiscussionId(null);
    }
  };

  const startCommentEdit = (comment: Comment) => {
    setEditingCommentId(comment.id);
    setEditingCommentContent(comment.content);
  };

  const handleSaveComment = async () => {
    if (!editingCommentId || !editingCommentContent.trim() || !selectedDiscussion) return;

    setSavingComment(true);
    try {
      await updateComment(editingCommentId, editingCommentContent.trim());
      const refreshedComments = await getComments(selectedDiscussion.id);
      setComments(refreshedComments);
      setEditingCommentId(null);
      setEditingCommentContent('');
      setSnackbarMessage('Comment updated successfully.');
      setSnackbarOpen(true);
    } catch (error: any) {
      setSnackbarMessage(error?.message || 'Failed to update comment.');
      setSnackbarOpen(true);
    } finally {
      setSavingComment(false);
    }
  };

  const handleDeleteComment = async (comment: Comment) => {
    if (!selectedDiscussion) return;
    if (!window.confirm('Delete this comment?')) {
      return;
    }

    setDeletingCommentId(comment.id);
    try {
      await deleteComment(comment.id, selectedDiscussion.id);
      const refreshedComments = await getComments(selectedDiscussion.id);
      setComments(refreshedComments);
      if (editingCommentId === comment.id) {
        setEditingCommentId(null);
        setEditingCommentContent('');
      }
      setSnackbarMessage('Comment deleted successfully.');
      setSnackbarOpen(true);
    } catch (error: any) {
      setSnackbarMessage(error?.message || 'Failed to delete comment.');
      setSnackbarOpen(true);
    } finally {
      setDeletingCommentId(null);
    }
  };

  const openReportDialog = (target: ReportTarget) => {
    if (!isAuthenticated) {
      setSnackbarMessage('Sign in to report content.');
      setSnackbarOpen(true);
      return;
    }

    setReportTarget(target);
    setReportReason('misinformation');
    setReportDetails('');
    setReportDialogOpen(true);
  };

  const handleSubmitReport = async () => {
    if (!user || !reportTarget) return;

    setReportSubmitting(true);
    try {
      const { error } =
        reportTarget.type === 'discussion'
          ? await supabase.from('community_reports').insert({
              reporter_id: user.id,
              target_type: 'discussion',
              discussion_id: reportTarget.id,
              reason: reportReason,
              details: reportDetails.trim() || null,
            })
          : await supabase.from('community_reports').insert({
              reporter_id: user.id,
              target_type: 'comment',
              comment_id: reportTarget.id,
              reason: reportReason,
              details: reportDetails.trim() || null,
            });
      if (error) throw error;

      setReportDialogOpen(false);
      setReportTarget(null);
      setSnackbarMessage('Report submitted for admin review.');
      setSnackbarOpen(true);

      if (isAdmin) {
        void loadCommunityWorkflows();
      }
    } catch (error: any) {
      setSnackbarMessage(
        isTableMissingError(error)
          ? 'Reporting is not available yet because the latest community migration has not been applied.'
          : error?.message || 'Failed to submit report.',
      );
      setSnackbarOpen(true);
    } finally {
      setReportSubmitting(false);
    }
  };

  const handleSubmitExpertRequest = async () => {
    if (!user) return;

    setExpertSubmitting(true);
    try {
      const payload = {
        user_id: user.id,
        headline: expertHeadline.trim() || null,
        bio: expertBio.trim() || null,
        expertise_tags: expertTags.split(',').map((tag) => tag.trim()).filter(Boolean),
        counties: expertCounties.split(',').map((county) => county.trim()).filter(Boolean),
        verification_status: 'pending',
        reviewed_by: null,
        reviewed_at: null,
        rejection_reason: null,
      };

      const { error } = myExpertProfile
        ? await supabase
            .from('community_expert_profiles')
            .update(payload)
            .eq('user_id', user.id)
        : await supabase.from('community_expert_profiles').insert(payload);

      if (error) throw error;

      setExpertDialogOpen(false);
      setSnackbarMessage('Expert verification request submitted.');
      setSnackbarOpen(true);
      await loadCommunityWorkflows();
    } catch (error: any) {
      setSnackbarMessage(
        isTableMissingError(error)
          ? 'Expert verification is not available yet because the latest community migration has not been applied.'
          : error?.message || 'Failed to submit expert request.',
      );
      setSnackbarOpen(true);
    } finally {
      setExpertSubmitting(false);
    }
  };

  const handleReviewExpertRequest = async (
    expertUserId: string,
    status: 'approved' | 'rejected',
  ) => {
    if (!user || !isAdmin) return;

    try {
      const { error } = await supabase
        .from('community_expert_profiles')
        .update({
          verification_status: status,
          reviewed_by: user.id,
          reviewed_at: new Date().toISOString(),
          rejection_reason: status === 'rejected' ? 'Please refine your profile details before resubmitting.' : null,
        })
        .eq('user_id', expertUserId);

      if (error) throw error;
      setSnackbarMessage(`Expert request ${status}.`);
      setSnackbarOpen(true);
      await loadCommunityWorkflows();
    } catch (error: any) {
      setSnackbarMessage(error?.message || 'Failed to review expert request.');
      setSnackbarOpen(true);
    }
  };

  const handleSubmitFarmerVerification = async () => {
    if (!user) return;

    if (!farms.length) {
      setSnackbarMessage('Create at least one farm first before requesting Verified Farmer status.');
      setSnackbarOpen(true);
      return;
    }

    setFarmerSubmitting(true);
    try {
      const payload = {
        user_id: user.id,
        verification_status: 'pending',
        request_notes: farmerRequestNotes.trim() || null,
        farm_count_snapshot: farms.length,
        reviewed_by: null,
        reviewed_at: null,
        rejection_reason: null,
      };

      const { error } = myFarmerVerification
        ? await supabase
            .from('community_farmer_verifications')
            .update(payload)
            .eq('user_id', user.id)
        : await supabase.from('community_farmer_verifications').insert(payload);

      if (error) throw error;

      setFarmerDialogOpen(false);
      setSnackbarMessage('Farmer verification request submitted.');
      setSnackbarOpen(true);
      await loadCommunityWorkflows();
    } catch (error: any) {
      setSnackbarMessage(
        isTableMissingError(error)
          ? 'Farmer verification is not available yet because the latest community migration has not been applied.'
          : error?.message || 'Failed to submit farmer verification request.',
      );
      setSnackbarOpen(true);
    } finally {
      setFarmerSubmitting(false);
    }
  };

  const handleReviewFarmerVerification = async (
    targetUserId: string,
    status: 'approved' | 'rejected',
  ) => {
    if (!user || !isAdmin) return;

    const rejectionReason =
      status === 'rejected'
        ? window.prompt('Rejection reason (optional):', 'Please provide more evidence about your farm ownership and activity.') || null
        : null;

    try {
      const { error } = await supabase
        .from('community_farmer_verifications')
        .update({
          verification_status: status,
          reviewed_by: user.id,
          reviewed_at: new Date().toISOString(),
          rejection_reason: status === 'rejected' ? rejectionReason : null,
        })
        .eq('user_id', targetUserId);

      if (error) throw error;

      if (status === 'approved') {
        setVerifiedFarmerByUserId((prev) => ({ ...prev, [targetUserId]: true }));
      }

      setSnackbarMessage(`Farmer verification ${status}.`);
      setSnackbarOpen(true);
      await loadCommunityWorkflows();
    } catch (error: any) {
      setSnackbarMessage(error?.message || 'Failed to review farmer verification request.');
      setSnackbarOpen(true);
    }
  };

  const openAdminReviewDialog = (report: CommunityReport, action: AdminReviewAction) => {
    setAdminReviewTarget(report);
    setAdminReviewAction(action);
    setAdminReviewNotes(
      action === 'dismissed'
        ? 'Dismissed after review.'
        : action === 'reviewed'
          ? 'Reviewed by admin.'
          : action === 'resolved'
            ? 'Resolved by admin review.'
            : `${report.targetType} ${action} by admin moderation.`,
    );
    setAdminReviewDialogOpen(true);
  };

  const handleReviewReport = async (
    reportId: string,
    status: 'reviewed' | 'resolved' | 'dismissed',
    resolutionNotes?: string,
  ) => {
    if (!user || !isAdmin) return;

    try {
      const { error } = await supabase
        .from('community_reports')
        .update({
          status,
          reviewed_by: user.id,
          reviewed_at: new Date().toISOString(),
          resolution_notes:
            resolutionNotes?.trim() ||
            (status === 'dismissed' ? 'Dismissed after review.' : 'Reviewed by admin.'),
        })
        .eq('id', reportId);

      if (error) throw error;
      setSnackbarMessage(`Report marked as ${status}.`);
      setSnackbarOpen(true);
      await loadCommunityWorkflows();
    } catch (error: any) {
      setSnackbarMessage(error?.message || 'Failed to review report.');
      setSnackbarOpen(true);
    }
  };

  const handleModerateReportedContent = async (
    report: CommunityReport,
    moderationStatus: 'hidden' | 'removed',
    resolutionNotes?: string,
  ) => {
    if (!user || !isAdmin) return;

    try {
      const nowIso = new Date().toISOString();
      const moderationPayload = {
        moderation_status: moderationStatus,
        moderation_reason: report.reason,
        moderated_by: user.id,
        moderated_at: nowIso,
      };

      if (report.targetType === 'discussion' && report.discussionId) {
        const { error } = await supabase
          .from('discussions')
          .update(moderationPayload)
          .eq('id', report.discussionId);
        if (error) throw error;
      }

      if (report.targetType === 'comment' && report.commentId) {
        const { error } = await supabase
          .from('discussion_comments')
          .update(moderationPayload)
          .eq('id', report.commentId);
        if (error) throw error;
      }

      const { error: reportError } = await supabase
        .from('community_reports')
        .update({
          status: 'resolved',
          reviewed_by: user.id,
          reviewed_at: nowIso,
          resolution_notes:
            resolutionNotes?.trim() ||
            `${report.targetType} ${moderationStatus} by admin moderation.`,
        })
        .eq('id', report.id);

      if (reportError) throw reportError;

      setSnackbarMessage(`${report.targetType === 'discussion' ? 'Discussion' : 'Comment'} ${moderationStatus}.`);
      setSnackbarOpen(true);
      await Promise.all([refreshDiscussions(), loadCommunityWorkflows()]);

      if (selectedDiscussion) {
        const refreshedComments = await getComments(selectedDiscussion.id);
        setComments(refreshedComments);
      }
    } catch (error: any) {
      setSnackbarMessage(error?.message || 'Failed to moderate content.');
      setSnackbarOpen(true);
    }
  };

  const handleSubmitAdminReview = async () => {
    if (!adminReviewTarget) return;

    setAdminReviewSubmitting(true);
    try {
      if (adminReviewAction === 'hidden' || adminReviewAction === 'removed') {
        await handleModerateReportedContent(adminReviewTarget, adminReviewAction, adminReviewNotes);
      } else {
        await handleReviewReport(adminReviewTarget.id, adminReviewAction, adminReviewNotes);
      }
      setAdminReviewDialogOpen(false);
      setAdminReviewTarget(null);
    } finally {
      setAdminReviewSubmitting(false);
    }
  };

  // Handle search
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  // Handle sort menu
  const handleSortClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setAnchorEl(null);
  };

  const handleSortSelect = (option: string) => {
    setSortOption(option);
    handleSortClose();
  };

  // Handle AI query submission
  const handleAiQuerySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!aiQuery.trim()) return;

    setAiLoading(true);
    try {
      setAiResponse(
        buildCommunityAssistantResponse(
          aiQuery,
          discussions,
          contributorById,
          expertByUserId,
          approvedExperts,
        ),
      );
    } finally {
      setAiLoading(false);
    }
  };

  const contributorSummaries = useMemo(() => {
    const map = new Map<string, ContributorSummary>();

    discussions.forEach((discussion) => {
      const existing = map.get(discussion.author.id) || {
        id: discussion.author.id,
        name: discussion.author.name,
        avatar: discussion.author.avatar,
        location: discussion.author.location,
        role: discussion.author.role || null,
        posts: 0,
        likes: 0,
        comments: 0,
        engagement: 0,
      };

      existing.posts += 1;
      existing.likes += discussion.likes;
      existing.comments += discussion.comments;
      existing.engagement = existing.likes + existing.comments;
      map.set(discussion.author.id, existing);
    });

    return Array.from(map.values()).sort(
      (a, b) => b.engagement - a.engagement || b.posts - a.posts || a.name.localeCompare(b.name),
    );
  }, [discussions]);

  const contributorById = useMemo(
    () => new Map(contributorSummaries.map((contributor) => [contributor.id, contributor])),
    [contributorSummaries],
  );

  const expertByUserId = useMemo(
    () => new Map(approvedExperts.map((expert) => [expert.userId, expert])),
    [approvedExperts],
  );

  useEffect(() => {
    if (!myExpertProfile) return;
    setExpertHeadline(myExpertProfile.headline || '');
    setExpertBio(myExpertProfile.bio || '');
    setExpertTags(myExpertProfile.expertiseTags.join(', '));
    setExpertCounties(myExpertProfile.counties.join(', '));
  }, [myExpertProfile]);

  useEffect(() => {
    setFarmerRequestNotes(myFarmerVerification?.requestNotes || '');
  }, [myFarmerVerification]);

  const popularTopics = useMemo(() => {
    const topicCounts = new Map<string, number>();

    discussions.forEach((discussion) => {
      discussion.tags.forEach((tag) => {
        const normalized = tag.trim();
        if (!normalized) return;
        topicCounts.set(normalized, (topicCounts.get(normalized) || 0) + 1);
      });
    });

    return Array.from(topicCounts.entries())
      .map(([topic, count]) => ({ topic, count }))
      .sort((a, b) => b.count - a.count || a.topic.localeCompare(b.topic))
      .slice(0, 6);
  }, [discussions]);

  const myPosts = useMemo(
    () => discussions.filter((discussion) => discussion.author.id === user?.id),
    [discussions, user?.id],
  );

  const needsResponseDiscussions = useMemo(
    () =>
      discussions
        .filter((discussion) => discussion.comments === 0)
        .sort(
          (a, b) =>
            b.views - a.views ||
            new Date(a.date).getTime() - new Date(b.date).getTime(),
        )
        .slice(0, 5),
    [discussions],
  );

  const postsThisWeek = useMemo(() => {
    const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return discussions.filter((discussion) => new Date(discussion.date).getTime() >= oneWeekAgo).length;
  }, [discussions]);

  const communityStats = useMemo(
    () => ({
      totalContributors: contributorSummaries.length,
      activeDiscussions: discussions.length,
      totalComments: discussions.reduce((sum, discussion) => sum + discussion.comments, 0),
      postsThisWeek,
    }),
    [contributorSummaries.length, discussions, postsThisWeek],
  );

  const moderationQueue = useMemo<ModerationQueueItem[]>(() => {
    const reportsByTarget = new Map<string, CommunityReport[]>();

    communityReports.forEach((report) => {
      const targetId = report.targetType === 'discussion' ? report.discussionId : report.commentId;
      const targetKey = `${report.targetType}:${targetId || report.id}`;
      const existing = reportsByTarget.get(targetKey) || [];
      existing.push(report);
      reportsByTarget.set(targetKey, existing);
    });

    return communityReports
      .map((report) => {
        const targetId = report.targetType === 'discussion' ? report.discussionId : report.commentId;
        const targetKey = `${report.targetType}:${targetId || report.id}`;
        const relatedReports = reportsByTarget.get(targetKey) || [report];
        const uniqueReporterCount = new Set(relatedReports.map((item) => item.reporterName)).size;
        const reasonSet = new Set(relatedReports.map((item) => item.reason));
        const oldestCreatedAt = Math.min(...relatedReports.map((item) => new Date(item.createdAt).getTime()));
        const ageHours = Math.max(0, (Date.now() - oldestCreatedAt) / (1000 * 60 * 60));
        const escalationReasons: string[] = [];
        let priorityScore = 0;

        if (reasonSet.has('unsafe_advice') || reasonSet.has('harassment') || reasonSet.has('abuse')) {
          priorityScore += 4;
          escalationReasons.push('Contains a high-risk safety or abuse report.');
        }

        if (relatedReports.length >= 3 || uniqueReporterCount >= 3) {
          priorityScore += 3;
          escalationReasons.push('Reported repeatedly by multiple community members.');
        } else if (relatedReports.length === 2 || uniqueReporterCount === 2) {
          priorityScore += 2;
          escalationReasons.push('Has repeat reports and should be reviewed sooner.');
        }

        if (ageHours >= 72) {
          priorityScore += 2;
          escalationReasons.push('Has been unresolved for more than 72 hours.');
        } else if (ageHours >= 24) {
          priorityScore += 1;
          escalationReasons.push('Has been waiting in the queue for more than 24 hours.');
        }

        if (report.status === 'reviewed' && report.targetModerationStatus === 'visible') {
          priorityScore += 1;
          escalationReasons.push('Already reviewed once but still needs a final decision.');
        }

        if (report.targetModerationStatus === 'hidden') {
          priorityScore = Math.max(priorityScore - 1, 0);
        } else if (report.targetModerationStatus === 'removed') {
          priorityScore = Math.max(priorityScore - 2, 0);
        }

        const priority: ModerationPriority =
          priorityScore >= 7 ? 'critical' : priorityScore >= 5 ? 'high' : priorityScore >= 3 ? 'medium' : 'low';

        if (escalationReasons.length === 0) {
          escalationReasons.push('Single report with no urgent escalation signal yet.');
        }

        return {
          ...report,
          targetKey,
          relatedReportCount: relatedReports.length,
          uniqueReporterCount,
          priority,
          priorityScore,
          escalationReasons,
        };
      })
      .sort(
        (a, b) =>
          PRIORITY_SORT_ORDER[a.priority] - PRIORITY_SORT_ORDER[b.priority] ||
          b.relatedReportCount - a.relatedReportCount ||
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      );
  }, [communityReports]);

  const moderationQueueStats = useMemo(
    () => ({
      critical: moderationQueue.filter((report) => report.priority === 'critical').length,
      high: moderationQueue.filter((report) => report.priority === 'high').length,
      repeatTargets: moderationQueue.filter((report) => report.relatedReportCount > 1).length,
      visibleTargets: moderationQueue.filter((report) => report.targetModerationStatus === 'visible').length,
    }),
    [moderationQueue],
  );

  const filteredDiscussions = useMemo(() => {
    let filtered = [...discussions];

    if (searchTerm) {
      const lowered = searchTerm.toLowerCase();
      filtered = filtered.filter((discussion) =>
        discussion.title.toLowerCase().includes(lowered) ||
        discussion.content.toLowerCase().includes(lowered) ||
        discussion.tags.some((tag) => tag.toLowerCase().includes(lowered)),
      );
    }

    if (tabValue === 1) {
      filtered = user ? filtered.filter((discussion) => discussion.author.id === user.id) : [];
    } else if (tabValue === 2) {
      filtered = filtered.filter((discussion) => savedPosts.includes(discussion.id));
    }

    if (filterMode === 'unanswered' && tabValue !== 2) {
      filtered = filtered.filter((discussion) => discussion.comments === 0);
    }

    if (sortOption === 'recent') {
      filtered.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    } else if (sortOption === 'popular') {
      filtered.sort((a, b) => b.likes - a.likes);
    } else if (sortOption === 'commented') {
      filtered.sort((a, b) => b.comments - a.comments);
    }

    return filtered;
  }, [discussions, filterMode, savedPosts, searchTerm, sortOption, tabValue, user]);

  // Format date to relative time
  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;
    
    return date.toLocaleDateString();
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Community Hub
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Refresh />}
            onClick={() => {
              void refreshDiscussions();
              void loadCommunityWorkflows();
            }}
            disabled={communityLoading}
          >
            Refresh
          </Button>
          <VisitorMetrics variant="compact" />
        </Box>
      </Box>

      {workflowWarnings.map((warning) => (
        <Alert key={warning} severity="warning" sx={{ mb: 2 }}>
          {warning}
        </Alert>
      ))}

      <Paper sx={{ mb: 4 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Discussions" />
          <Tab label="My Posts" />
          <Tab label="Saved" />
          <Tab label="AI Assistant" />
        </Tabs>

        <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search discussions by title, content, or tags"
                value={searchTerm}
                onChange={handleSearch}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <Search sx={{ color: 'action.active', mr: 1 }} />
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button
                  variant={filterMode === 'unanswered' ? 'contained' : 'outlined'}
                  startIcon={<FilterList />}
                  onClick={() => setFilterMode((current) => (current === 'unanswered' ? 'all' : 'unanswered'))}
                >
                  {filterMode === 'unanswered' ? 'All Posts' : 'Needs Response'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<MoreVert />}
                  onClick={handleSortClick}
                >
                  Sort
                </Button>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleSortClose}
                >
                  <MenuItem onClick={() => handleSortSelect('recent')} selected={sortOption === 'recent'}>
                    Most Recent
                  </MenuItem>
                  <MenuItem onClick={() => handleSortSelect('popular')} selected={sortOption === 'popular'}>
                    Most Popular
                  </MenuItem>
                  <MenuItem onClick={() => handleSortSelect('commented')} selected={sortOption === 'commented'}>
                    Most Commented
                  </MenuItem>
                </Menu>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Main content area */}
        <Grid item xs={12} md={8}>
          <TabPanel value={tabValue} index={0}>
            {/* New Post Button */}
            {isAuthenticated && (
              <Button 
                variant="contained" 
                color="primary" 
                fullWidth 
                sx={{ mb: 3 }}
                onClick={() => {
                  resetDiscussionComposer();
                  setTabValue(3);
                }}
              >
                Start a New Discussion
              </Button>
            )}
            
            {/* Discussions List */}
            {communityLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : filteredDiscussions.length > 0 ? (
              <Box>
                {filteredDiscussions.map((discussion) => (
                  <Card key={discussion.id} sx={{ mb: 3 }}>
                    <CardHeader
                      avatar={
                        <Avatar src={discussion.author.avatar} alt={discussion.author.name} />
                      }
                      action={
                        <IconButton aria-label="settings">
                          <MoreVert />
                        </IconButton>
                      }
                      title={discussion.author.name}
                      subheader={`${discussion.author.location} • ${formatRelativeTime(discussion.date)}`}
                    />
                    <CardContent>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        <Chip
                          label={getContributorBadge(
                            discussion,
                            contributorById.get(discussion.author.id),
                            expertByUserId.get(discussion.author.id),
                          ).label}
                          size="small"
                          color={getContributorBadge(
                            discussion,
                            contributorById.get(discussion.author.id),
                            expertByUserId.get(discussion.author.id),
                          ).color}
                        />
                        {verifiedFarmerByUserId[discussion.author.id] && (
                          <Chip
                            icon={<Verified />}
                            label="Verified Farmer"
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        )}
                        {discussion.comments === 0 && (
                          <Chip label="Needs Response" size="small" color="warning" variant="outlined" />
                        )}
                        {discussion.moderationStatus && discussion.moderationStatus !== 'visible' && (
                          <Chip
                            label={`Moderated: ${discussion.moderationStatus}`}
                            size="small"
                            color={discussion.moderationStatus === 'removed' ? 'error' : 'warning'}
                            variant="outlined"
                          />
                        )}
                      </Box>
                      <Typography variant="h6" gutterBottom>
                        {discussion.title}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" paragraph>
                        {discussion.content}
                      </Typography>
                      {Array.isArray(discussion.media) && discussion.media.length > 0 && (
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: discussion.media.length > 1 ? 'repeat(3, 1fr)' : '1fr',
                            gap: 1,
                            mb: 2,
                          }}
                        >
                          {discussion.media.slice(0, 3).map((media) =>
                            media.mediaKind === 'video' ? (
                              <Box
                                key={media.storagePath}
                                component="video"
                                src={media.url}
                                controls
                                muted
                                playsInline
                                sx={{ width: '100%', borderRadius: 2, bgcolor: 'black' }}
                              />
                            ) : (
                              <Box
                                key={media.storagePath}
                                component="img"
                                src={media.url}
                                alt="Community media"
                                sx={{ width: '100%', height: 180, objectFit: 'cover', borderRadius: 2 }}
                              />
                            ),
                          )}
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        {discussion.tags.map((tag, index) => (
                          <Chip key={index} label={tag} size="small" color="primary" variant="outlined" />
                        ))}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                          <ThumbUp fontSize="small" sx={{ mr: 0.5 }} />
                          <Typography variant="body2">{discussion.likes}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                          <CommentIcon fontSize="small" sx={{ mr: 0.5 }} />
                          <Typography variant="body2">{discussion.comments}</Typography>
                        </Box>
                        <Typography variant="body2" sx={{ ml: 'auto' }}>
                          {discussion.views} views
                        </Typography>
                      </Box>
                    </CardContent>
                    <Divider />
                    <CardActions>
                      <Button 
                        startIcon={<ThumbUp />} 
                        size="small" 
                        onClick={() => toggleLikePost(discussion.id)}
                        color={discussion.isLiked ? "primary" : "inherit"}
                      >
                        Like ({discussion.likes})
                      </Button>
                      <Button 
                        startIcon={<CommentIcon />} 
                        size="small"
                        onClick={() => handleViewPost(discussion)}
                      >
                        Comment ({discussion.comments})
                      </Button>
                      <Button 
                        startIcon={<Share />} 
                        size="small"
                        onClick={() => handleShare(discussion.title)}
                      >
                        Share
                      </Button>
                      <Button
                        startIcon={<FlagOutlined />}
                        size="small"
                        onClick={() =>
                          openReportDialog({
                            type: 'discussion',
                            id: discussion.id,
                            title: discussion.title,
                          })
                        }
                      >
                        Report
                      </Button>
                      {discussion.author.id === user?.id && (
                        <Button
                          startIcon={<Edit />}
                          size="small"
                          onClick={() => startDiscussionEdit(discussion)}
                        >
                          Edit
                        </Button>
                      )}
                      {discussion.author.id === user?.id && (
                        <Button
                          startIcon={<Delete />}
                          size="small"
                          color="error"
                          disabled={deletingDiscussionId === discussion.id}
                          onClick={() => void handleDeleteDiscussion(discussion)}
                        >
                          {deletingDiscussionId === discussion.id ? 'Deleting...' : 'Delete'}
                        </Button>
                      )}
                      <IconButton 
                        sx={{ ml: 'auto' }} 
                        onClick={() => toggleSavePost(discussion.id)}
                        color={savedPosts.includes(discussion.id) ? 'primary' : 'default'}
                      >
                        {savedPosts.includes(discussion.id) ? <Bookmark /> : <BookmarkBorder />}
                      </IconButton>
                    </CardActions>
                  </Card>
                ))}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', p: 4 }}>
                <Typography variant="h6" color="text.secondary">
                  No discussions found matching your criteria
                </Typography>
                <Button variant="outlined" sx={{ mt: 2 }} onClick={() => setSearchTerm('')}>
                  Clear Search
                </Button>
              </Box>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {isAuthenticated ? (
              myPosts.length > 0 ? (
                <Box>
                  {myPosts.map((discussion) => (
                    <Card key={discussion.id} sx={{ mb: 3 }}>
                      <CardHeader
                        avatar={<Avatar src={discussion.author.avatar} alt={discussion.author.name} />}
                        title={discussion.title}
                        subheader={`${formatRelativeTime(discussion.date)} • ${discussion.comments} comments • ${discussion.likes} likes`}
                      />
                      <CardContent>
                        <Typography variant="body2" color="text.secondary" paragraph>
                          {discussion.content.length > 240
                            ? `${discussion.content.substring(0, 240)}...`
                            : discussion.content}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {discussion.tags.map((tag, index) => (
                            <Chip key={index} label={tag} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </CardContent>
                      <CardActions>
                        <Button size="small" onClick={() => handleViewPost(discussion)}>
                          View Discussion
                        </Button>
                        <Button size="small" startIcon={<Edit />} onClick={() => startDiscussionEdit(discussion)}>
                          Edit
                        </Button>
                        <Button
                          size="small"
                          startIcon={<Delete />}
                          color="error"
                          disabled={deletingDiscussionId === discussion.id}
                          onClick={() => void handleDeleteDiscussion(discussion)}
                        >
                          {deletingDiscussionId === discussion.id ? 'Deleting...' : 'Delete'}
                        </Button>
                      </CardActions>
                    </Card>
                  ))}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', p: 4 }}>
                  <Typography variant="h6" gutterBottom>
                    You haven't created any posts yet
                  </Typography>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    sx={{ mt: 2 }}
                    onClick={() => setTabValue(3)}
                  >
                    Create Your First Post
                  </Button>
                </Box>
              )
            ) : (
              <Box sx={{ textAlign: 'center', p: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Sign in to view your posts
                </Typography>
                <Button variant="contained" color="primary" sx={{ mt: 2 }}>
                  Sign In
                </Button>
              </Box>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            {isAuthenticated ? (
              savedPosts.length > 0 ? (
                <Box>
                  {filteredDiscussions.map((discussion) => (
                    <Card key={discussion.id} sx={{ mb: 3 }}>
                      <CardHeader
                        avatar={
                          <Avatar src={discussion.author.avatar} alt={discussion.author.name} />
                        }
                        title={discussion.author.name}
                        subheader={formatRelativeTime(discussion.date)}
                      />
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          {discussion.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {discussion.content.length > 200
                            ? `${discussion.content.substring(0, 200)}...`
                            : discussion.content}
                        </Typography>
                      </CardContent>
                      <CardActions>
                        <Button 
                          size="small" 
                          color="primary"
                          onClick={() => handleViewPost(discussion)}
                        >
                          View Full Post
                        </Button>
                        <IconButton 
                          sx={{ ml: 'auto' }} 
                          onClick={() => toggleSavePost(discussion.id)}
                          color="primary"
                        >
                          <Bookmark />
                        </IconButton>
                      </CardActions>
                    </Card>
                  ))}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', p: 4 }}>
                  <Typography variant="h6" gutterBottom>
                    You haven't saved any posts yet
                  </Typography>
                  <Typography variant="body1" paragraph>
                    When you find interesting discussions, click the bookmark icon to save them for later.
                  </Typography>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    sx={{ mt: 2 }}
                    onClick={() => setTabValue(0)}
                  >
                    Browse Discussions
                  </Button>
                </Box>
              )
            ) : (
              <Box sx={{ textAlign: 'center', p: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Sign in to view your saved posts
                </Typography>
                <Button variant="contained" color="primary" sx={{ mt: 2 }}>
                  Sign In
                </Button>
              </Box>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                {/* Create New Post Form */}
                <Paper sx={{ p: 3, mb: { xs: 3, md: 0 } }}>
                  <Typography variant="h6" gutterBottom>
                    {composerMode === 'edit' ? 'Edit Discussion' : 'Start a New Discussion'}
                  </Typography>
                  {isAuthenticated ? (
                    <form onSubmit={handleSaveDiscussion}>
                      <TextField
                        label="Title"
                        fullWidth
                        margin="normal"
                        value={newPostTitle}
                        onChange={(e) => setNewPostTitle(e.target.value)}
                        required
                      />
                      <TextField
                        label="Content"
                        fullWidth
                        margin="normal"
                        multiline
                        rows={6}
                        value={newPostContent}
                        onChange={(e) => setNewPostContent(e.target.value)}
                        required
                      />
                      <TextField
                        label="Tags (comma separated)"
                        fullWidth
                        margin="normal"
                        placeholder="e.g. Maize, Irrigation, Market Prices"
                        value={newPostTags}
                        onChange={(e) => setNewPostTags(e.target.value)}
                      />
                      <FormControl fullWidth size="small" margin="normal">
                        <InputLabel id="community-post-type-label">Post Type</InputLabel>
                        <Select
                          labelId="community-post-type-label"
                          value={composerPostType}
                          label="Post Type"
                          onChange={(event) =>
                            setComposerPostType(String(event.target.value) as any)
                          }
                        >
                          <MenuItem value="text">Text (auto)</MenuItem>
                          <MenuItem value="farm_update">Farm Update</MenuItem>
                          <MenuItem value="photo">Photo</MenuItem>
                          <MenuItem value="photo_carousel">Photo Carousel</MenuItem>
                          <MenuItem value="video">Video</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl fullWidth size="small" margin="normal" disabled={!farms.length}>
                        <InputLabel id="community-farm-link-label">Link Farm</InputLabel>
                        <Select
                          labelId="community-farm-link-label"
                          value={composerFarmId}
                          label="Link Farm"
                          onChange={(event) => setComposerFarmId(String(event.target.value))}
                        >
                          <MenuItem value="">No farm link</MenuItem>
                          {farms.map((farm) => (
                            <MenuItem key={farm.id} value={farm.id}>
                              {farm.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button variant="outlined" component="label" fullWidth sx={{ mt: 1 }}>
                        Add Photos / Videos
                        <input
                          hidden
                          type="file"
                          multiple
                          accept="image/*,video/*"
                          onChange={handleComposerMediaChange}
                        />
                      </Button>
                      {composerMediaPreviews.length > 0 && (
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: 1,
                            mt: 2,
                          }}
                        >
                          {composerMediaPreviews.map((preview, idx) =>
                            preview.kind === 'video' ? (
                              <Box
                                key={`${preview.url}-${idx}`}
                                component="video"
                                src={preview.url}
                                controls
                                muted
                                playsInline
                                sx={{ width: '100%', borderRadius: 2, bgcolor: 'black' }}
                              />
                            ) : (
                              <Box
                                key={`${preview.url}-${idx}`}
                                component="img"
                                src={preview.url}
                                alt="Community media"
                                sx={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 2 }}
                              />
                            ),
                          )}
                        </Box>
                      )}
                      {composerMediaPreviews.length > 0 && (
                        <Button variant="text" fullWidth sx={{ mt: 1 }} onClick={clearComposerMedia}>
                          Remove Media
                        </Button>
                      )}
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        fullWidth
                        sx={{ mt: 2 }}
                        disabled={submittingDiscussion || composerMediaUploading}
                      >
                        {submittingDiscussion
                          ? composerMode === 'edit'
                            ? 'Saving...'
                            : 'Posting...'
                          : composerMode === 'edit'
                            ? 'Save Changes'
                            : 'Post Discussion'}
                      </Button>
                      {composerMode === 'edit' && (
                        <Button
                          type="button"
                          variant="text"
                          fullWidth
                          sx={{ mt: 1 }}
                          onClick={resetDiscussionComposer}
                        >
                          Cancel Editing
                        </Button>
                      )}
                    </form>
                  ) : (
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="body1" paragraph>
                        You need to be signed in to create a post.
                      </Typography>
                      <Button variant="contained" color="primary">
                        Sign In
                      </Button>
                    </Box>
                  )}
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={6}>
                {/* AI Assistant */}
                <Paper sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <SmartToy color="primary" sx={{ mr: 1, fontSize: 28 }} />
                    <Typography variant="h6">
                      AI Farming Assistant
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Ask questions about farming techniques, crop diseases, market trends, or best practices. Responses are grounded in visible community discussions, tags, counties, and verified expert profiles.
                  </Typography>
                  
                  {aiResponse && (
                    <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                        {aiResponse}
                      </Typography>
                    </Paper>
                  )}
                  
                  <form onSubmit={handleAiQuerySubmit}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <TextField
                        fullWidth
                        placeholder="Ask a farming question..."
                        value={aiQuery}
                        onChange={(e) => setAiQuery(e.target.value)}
                        variant="outlined"
                        multiline
                        rows={2}
                        disabled={aiLoading}
                      />
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        sx={{ ml: 1, minWidth: '56px', height: '56px' }}
                        disabled={!aiQuery.trim() || aiLoading}
                      >
                        {aiLoading ? <CircularProgress size={24} /> : <Send />}
                      </Button>
                    </Box>
                  </form>
                  
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Try asking about:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <Chip 
                        label="Drought resistant crops" 
                        size="small" 
                        onClick={() => setAiQuery('What are the best drought resistant maize varieties for Kenya?')} 
                      />
                      <Chip 
                        label="Tomato diseases" 
                        size="small" 
                        onClick={() => setAiQuery('How do I treat tomato blight in humid conditions?')} 
                      />
                      <Chip 
                        label="Solar irrigation" 
                        size="small" 
                        onClick={() => setAiQuery('Is solar irrigation worth the investment for a small farm?')} 
                      />
                      <Chip 
                        label="Avocado prices" 
                        size="small" 
                        onClick={() => setAiQuery('What are the current market prices for avocados?')} 
                      />
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </TabPanel>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Popular Topics */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Popular Topics
            </Typography>
            <List dense>
              {popularTopics.length > 0 ? popularTopics.map(({ topic, count }) => (
                <ListItem key={topic} disablePadding sx={{ py: 0.5 }}>
                  <ListItemText primary={topic} />
                  <Chip label={count} size="small" color="primary" variant="outlined" />
                </ListItem>
              )) : (
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemText primary="No tagged discussions yet" secondary="Tags will appear here as members post." />
                </ListItem>
              )}
            </List>
          </Paper>

          {/* Trusted Contributors */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Trusted Contributors
            </Typography>
            <List>
              {contributorSummaries.slice(0, 5).map((contributor) => (
                <ListItem key={contributor.id} sx={{ px: 0 }}>
                  <ListItemAvatar>
                    <Avatar src={contributor.avatar} alt={contributor.name} />
                  </ListItemAvatar>
                  <ListItemText 
                    primary={contributor.name}
                    secondary={`${contributor.posts} posts • ${contributor.engagement} total engagements`}
                  />
                  <Chip
                    label={getContributorBadge(
                      {
                        id: contributor.id,
                        title: '',
                        content: '',
                        author: contributor,
                        date: '',
                        tags: [],
                        likes: 0,
                        comments: 0,
                        views: 0,
                      },
                      contributor,
                      expertByUserId.get(contributor.id),
                    ).label}
                    size="small"
                    color={getContributorBadge(
                      {
                        id: contributor.id,
                        title: '',
                        content: '',
                        author: contributor,
                        date: '',
                        tags: [],
                        likes: 0,
                        comments: 0,
                        views: 0,
                      },
                      contributor,
                      expertByUserId.get(contributor.id),
                    ).color}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>

          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Verified Experts
            </Typography>
            {loadingWorkflows ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : approvedExperts.length > 0 ? (
              <List>
                {approvedExperts.slice(0, 5).map((expert) => (
                  <ListItem key={expert.userId} sx={{ px: 0, alignItems: 'flex-start' }}>
                    <ListItemAvatar>
                      <Avatar src={expert.avatar} alt={expert.name} />
                    </ListItemAvatar>
                    <ListItemText
                      primary={expert.name}
                      secondary={
                        <>
                          <Typography variant="body2" color="text.secondary">
                            {expert.headline || expert.location}
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                            {expert.expertiseTags.slice(0, 3).map((tag) => (
                              <Chip key={tag} label={tag} size="small" variant="outlined" color="secondary" />
                            ))}
                          </Box>
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No verified experts are approved yet.
              </Typography>
            )}
          </Paper>

          {/* Community Stats */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Community Stats
            </Typography>
            <List dense>
              <ListItem disablePadding sx={{ py: 0.5 }}>
                <ListItemText primary="Contributors" />
                <Typography variant="body1" fontWeight="bold">{communityStats.totalContributors}</Typography>
              </ListItem>
              <ListItem disablePadding sx={{ py: 0.5 }}>
                <ListItemText primary="Active Discussions" />
                <Typography variant="body1" fontWeight="bold">{communityStats.activeDiscussions}</Typography>
              </ListItem>
              <ListItem disablePadding sx={{ py: 0.5 }}>
                <ListItemText primary="Posts This Week" />
                <Typography variant="body1" fontWeight="bold">{communityStats.postsThisWeek}</Typography>
              </ListItem>
              <ListItem disablePadding sx={{ py: 0.5 }}>
                <ListItemText primary="Total Comments" />
                <Typography variant="body1" fontWeight="bold">{communityStats.totalComments}</Typography>
              </ListItem>
            </List>
          </Paper>

          <Paper sx={{ p: 2, mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Needs Expert Response
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Unanswered discussions that need guidance from admins, trusted contributors, or experienced farmers.
            </Typography>
            <List dense>
              {needsResponseDiscussions.length > 0 ? needsResponseDiscussions.map((discussion) => (
                <ListItem
                  key={discussion.id}
                  disablePadding
                  sx={{ py: 0.75, cursor: 'pointer' }}
                  onClick={() => handleViewPost(discussion)}
                >
                  <ListItemText
                    primary={discussion.title}
                    secondary={`${discussion.author.name} • ${discussion.views} views`}
                  />
                </ListItem>
              )) : (
                <ListItem disablePadding sx={{ py: 0.5 }}>
                  <ListItemText primary="All visible discussions already have responses." />
                </ListItem>
              )}
            </List>
          </Paper>

          {isAuthenticated && (
            <Paper sx={{ p: 2, mt: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Verified color="success" fontSize="small" />
                <Typography variant="h6">
                  Farmer Verification
                </Typography>
              </Box>
              {myFarmerVerification ? (
                <>
                  <Chip
                    label={`Status: ${myFarmerVerification.verificationStatus}`}
                    color={
                      myFarmerVerification.verificationStatus === 'approved'
                        ? 'success'
                        : myFarmerVerification.verificationStatus === 'pending'
                          ? 'warning'
                          : 'error'
                    }
                    size="small"
                    sx={{ mb: 1.5 }}
                  />
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {myFarmerVerification.verificationStatus === 'approved'
                      ? 'You are verified as a FarmersHub farmer. Your badge will appear across community posts and comments.'
                      : myFarmerVerification.requestNotes || 'Your farmer verification request is on file.'}
                  </Typography>
                  {myFarmerVerification.rejectionReason && (
                    <Alert severity="warning" sx={{ mb: 1.5 }}>
                      {myFarmerVerification.rejectionReason}
                    </Alert>
                  )}
                  <Button variant="outlined" fullWidth onClick={() => setFarmerDialogOpen(true)}>
                    {myFarmerVerification.verificationStatus === 'approved'
                      ? 'View Request'
                      : myFarmerVerification.verificationStatus === 'rejected'
                        ? 'Update Request'
                        : 'View Request'}
                  </Button>
                </>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Verified farmers get a trust badge across the community. You can request verification if you own at least one farm record.
                  </Typography>
                  <Button variant="contained" fullWidth onClick={() => setFarmerDialogOpen(true)}>
                    Request Farmer Verification
                  </Button>
                </>
              )}
            </Paper>
          )}

          {isAdmin && (
            <Paper sx={{ p: 2, mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Farmer Verification Queue
              </Typography>
              {pendingFarmerVerifications.length > 0 ? (
                <List dense>
                  {pendingFarmerVerifications.map((request) => (
                    <ListItem key={request.userId} disablePadding sx={{ py: 1, display: 'block' }}>
                      <Typography variant="subtitle2">{request.userId}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Farms snapshot: {request.farmCountSnapshot}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {request.requestNotes || 'No notes provided.'}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <Button size="small" variant="contained" onClick={() => void handleReviewFarmerVerification(request.userId, 'approved')}>
                          Approve
                        </Button>
                        <Button size="small" variant="outlined" color="error" onClick={() => void handleReviewFarmerVerification(request.userId, 'rejected')}>
                          Reject
                        </Button>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No pending farmer verification requests.
                </Typography>
              )}
            </Paper>
          )}

          {isAuthenticated && (
            <Paper sx={{ p: 2, mt: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Verified color="secondary" fontSize="small" />
                <Typography variant="h6">
                  Expert Verification
                </Typography>
              </Box>
              {myExpertProfile ? (
                <>
                  <Chip
                    label={`Status: ${myExpertProfile.verificationStatus}`}
                    color={
                      myExpertProfile.verificationStatus === 'approved'
                        ? 'success'
                        : myExpertProfile.verificationStatus === 'pending'
                          ? 'warning'
                          : 'error'
                    }
                    size="small"
                    sx={{ mb: 1.5 }}
                  />
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {myExpertProfile.headline || 'Your verification request is on file.'}
                  </Typography>
                  {myExpertProfile.rejectionReason && (
                    <Alert severity="warning" sx={{ mb: 1.5 }}>
                      {myExpertProfile.rejectionReason}
                    </Alert>
                  )}
                  <Button variant="outlined" fullWidth onClick={() => setExpertDialogOpen(true)}>
                    {myExpertProfile.verificationStatus === 'approved'
                      ? 'View Verified Profile'
                      : myExpertProfile.verificationStatus === 'rejected'
                        ? 'Update Request'
                        : 'View Request'}
                  </Button>
                </>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Share your agricultural expertise, counties served, and focus areas so admins can verify you as a trusted expert.
                  </Typography>
                  <Button variant="contained" fullWidth onClick={() => setExpertDialogOpen(true)}>
                    Request Verification
                  </Button>
                </>
              )}
            </Paper>
          )}

          {isAdmin && (
            <Paper sx={{ p: 2, mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Expert Review Queue
              </Typography>
              {pendingExperts.length > 0 ? (
                <List dense>
                  {pendingExperts.map((expert) => (
                    <ListItem key={expert.userId} disablePadding sx={{ py: 1, display: 'block' }}>
                      <Typography variant="subtitle2">{expert.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {expert.headline || 'No headline provided'}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, my: 1 }}>
                        {expert.expertiseTags.map((tag) => (
                          <Chip key={tag} label={tag} size="small" variant="outlined" />
                        ))}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button size="small" variant="contained" onClick={() => void handleReviewExpertRequest(expert.userId, 'approved')}>
                          Approve
                        </Button>
                        <Button size="small" variant="outlined" color="error" onClick={() => void handleReviewExpertRequest(expert.userId, 'rejected')}>
                          Reject
                        </Button>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No pending expert requests.
                </Typography>
              )}
            </Paper>
          )}

          {isAdmin && (
            <Paper sx={{ p: 2, mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Moderation Queue
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Queue priority now escalates automatically for high-risk report reasons, repeat reports, and unresolved items that stay open too long.
              </Typography>
              {moderationQueue.length > 0 ? (
                <>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    <Chip label={`${moderationQueueStats.critical} Critical`} color="error" size="small" />
                    <Chip label={`${moderationQueueStats.high} High`} color="warning" size="small" />
                    <Chip label={`${moderationQueueStats.repeatTargets} Repeat Reports`} color="secondary" size="small" variant="outlined" />
                    <Chip label={`${moderationQueueStats.visibleTargets} Still Visible`} color="info" size="small" variant="outlined" />
                  </Box>
                <List dense>
                  {moderationQueue.map((report) => (
                    <ListItem key={report.id} disablePadding sx={{ py: 1, display: 'block' }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="subtitle2">{report.targetTitle}</Typography>
                        <Chip
                          label={`Priority: ${report.priority}`}
                          size="small"
                          color={
                            report.priority === 'critical'
                              ? 'error'
                              : report.priority === 'high'
                                ? 'warning'
                                : report.priority === 'medium'
                                  ? 'secondary'
                                  : 'default'
                          }
                        />
                        {report.relatedReportCount > 1 && (
                          <Chip
                            label={`${report.relatedReportCount} reports / ${report.uniqueReporterCount} reporters`}
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                        )}
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {getReportReasonLabel(report.reason)} • Reported by {report.reporterName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        Escalation: {report.escalationReasons[0]}
                      </Typography>
                      {report.details && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {report.details}
                        </Typography>
                      )}
                      {report.resolutionNotes && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          Note: {report.resolutionNotes}
                        </Typography>
                      )}
                      {report.reviewedByName && report.reviewedAt && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          Last reviewed by {report.reviewedByName} on {new Date(report.reviewedAt).toLocaleString()}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        <Chip
                          label={`Content: ${report.targetModerationStatus}`}
                          size="small"
                          color={report.targetModerationStatus === 'removed' ? 'error' : report.targetModerationStatus === 'hidden' ? 'warning' : 'default'}
                        />
                        <Chip label={`Status: ${report.status}`} size="small" variant="outlined" />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <Button size="small" variant="outlined" onClick={() => openAdminReviewDialog(report, 'reviewed')}>
                          Review
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          disabled={report.targetModerationStatus === 'hidden' || report.targetModerationStatus === 'removed'}
                          onClick={() => openAdminReviewDialog(report, 'hidden')}
                        >
                          Hide
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          color="error"
                          disabled={report.targetModerationStatus === 'removed'}
                          onClick={() => openAdminReviewDialog(report, 'removed')}
                        >
                          Remove
                        </Button>
                        <Button size="small" variant="contained" onClick={() => openAdminReviewDialog(report, 'resolved')}>
                          Resolve
                        </Button>
                        <Button size="small" variant="text" color="inherit" onClick={() => openAdminReviewDialog(report, 'dismissed')}>
                          Dismiss
                        </Button>
                      </Box>
                    </ListItem>
                  ))}
                </List>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No open moderation reports.
                </Typography>
              )}
            </Paper>
          )}

          {isAdmin && (
            <Paper sx={{ p: 2, mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Recent Review History
              </Typography>
              {moderationHistory.length > 0 ? (
                <List dense>
                  {moderationHistory.map((report) => (
                    <ListItem key={report.id} disablePadding sx={{ py: 1, display: 'block' }}>
                      <Typography variant="subtitle2">{report.targetTitle}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {report.status} • {report.reason}
                      </Typography>
                      {report.resolutionNotes && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {report.resolutionNotes}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        {report.reviewedByName || 'Admin'} • {report.reviewedAt ? new Date(report.reviewedAt).toLocaleString() : 'Not recorded'}
                      </Typography>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No moderation history yet.
                </Typography>
              )}
            </Paper>
          )}
        </Grid>
      </Grid>
      {/* Post View Dialog */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
      >
        {selectedDiscussion && (
          <>
            <DialogTitle>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar src={selectedDiscussion.author.avatar} />
                <Box>
                  <Typography variant="h6">{selectedDiscussion.title}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Posted by {selectedDiscussion.author.name} • {formatRelativeTime(selectedDiscussion.date)}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      label={getContributorBadge(
                        selectedDiscussion,
                        contributorById.get(selectedDiscussion.author.id),
                        expertByUserId.get(selectedDiscussion.author.id),
                      ).label}
                      size="small"
                      color={getContributorBadge(
                        selectedDiscussion,
                        contributorById.get(selectedDiscussion.author.id),
                        expertByUserId.get(selectedDiscussion.author.id),
                      ).color}
                    />
                    {verifiedFarmerByUserId[selectedDiscussion.author.id] && (
                      <Chip
                        icon={<Verified />}
                        label="Verified Farmer"
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{ ml: 1 }}
                      />
                    )}
                    {selectedDiscussion.moderationStatus && selectedDiscussion.moderationStatus !== 'visible' && (
                      <Chip
                        label={`Moderated: ${selectedDiscussion.moderationStatus}`}
                        size="small"
                        color={selectedDiscussion.moderationStatus === 'removed' ? 'error' : 'warning'}
                        variant="outlined"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              {selectedDiscussion.moderationReason && selectedDiscussion.moderationStatus !== 'visible' && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Moderation reason: {selectedDiscussion.moderationReason}
                </Alert>
              )}
              <Typography variant="body1" paragraph>
                {selectedDiscussion.content}
              </Typography>
              {Array.isArray(selectedDiscussion.media) && selectedDiscussion.media.length > 0 && (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: selectedDiscussion.media.length > 1 ? 'repeat(3, 1fr)' : '1fr',
                    gap: 1,
                    mb: 2,
                  }}
                >
                  {selectedDiscussion.media.slice(0, 6).map((media) =>
                    media.mediaKind === 'video' ? (
                      <Box
                        key={media.storagePath}
                        component="video"
                        src={media.url}
                        controls
                        muted
                        playsInline
                        sx={{ width: '100%', borderRadius: 2, bgcolor: 'black' }}
                      />
                    ) : (
                      <Box
                        key={media.storagePath}
                        component="img"
                        src={media.url}
                        alt="Community media"
                        sx={{ width: '100%', height: 220, objectFit: 'cover', borderRadius: 2 }}
                      />
                    ),
                  )}
                </Box>
              )}
              <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                {selectedDiscussion.tags.map((tag: string, index: number) => (
                  <Chip key={index} label={tag} size="small" />
                ))}
              </Box>
              
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>Comments ({selectedDiscussion.comments})</Typography>

              <List>
                {comments.length === 0 ? (
                  <ListItem>
                    <ListItemText primary="No comments yet. Be the first to comment!" />
                  </ListItem>
                ) : (
                  comments.map((comment) => (
                    <React.Fragment key={comment.id}>
                      <ListItem alignItems="flex-start" sx={{ display: 'block' }}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <ListItemAvatar>
                            <Avatar alt={comment.author.name} src={comment.author.avatar} />
                          </ListItemAvatar>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                              <Typography variant="body2" fontWeight={600}>
                                {comment.author.name}
                              </Typography>
                              {expertByUserId.get(comment.author.id)?.verificationStatus === 'approved' && (
                                <Chip label="Verified Expert" size="small" color="secondary" />
                              )}
                              {verifiedFarmerByUserId[comment.author.id] && (
                                <Chip icon={<Verified />} label="Verified Farmer" size="small" color="success" variant="outlined" />
                              )}
                              {comment.moderationStatus && comment.moderationStatus !== 'visible' && (
                                <Chip
                                  label={`Moderated: ${comment.moderationStatus}`}
                                  size="small"
                                  color={comment.moderationStatus === 'removed' ? 'error' : 'warning'}
                                  variant="outlined"
                                />
                              )}
                            </Box>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                              {new Date(comment.date).toLocaleDateString()}
                            </Typography>
                            {editingCommentId === comment.id ? (
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1 }}>
                                <TextField
                                  fullWidth
                                  multiline
                                  minRows={2}
                                  value={editingCommentContent}
                                  onChange={(event) => setEditingCommentContent(event.target.value)}
                                />
                                <Button
                                  variant="contained"
                                  onClick={() => void handleSaveComment()}
                                  disabled={!editingCommentContent.trim() || savingComment}
                                >
                                  {savingComment ? 'Saving...' : 'Save'}
                                </Button>
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.primary">
                                {comment.content}
                                {comment.moderationReason && comment.moderationStatus !== 'visible' && (
                                  <> ({comment.moderationReason})</>
                                )}
                              </Typography>
                            )}
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                              <Button
                                size="small"
                                color="inherit"
                                startIcon={<FlagOutlined />}
                                onClick={() =>
                                  openReportDialog({
                                    type: 'comment',
                                    id: comment.id,
                                    title: `Comment by ${comment.author.name}`,
                                  })
                                }
                              >
                                Report
                              </Button>
                              {comment.author.id === user?.id && editingCommentId !== comment.id && (
                                <Button size="small" startIcon={<Edit />} onClick={() => startCommentEdit(comment)}>
                                  Edit
                                </Button>
                              )}
                              {comment.author.id === user?.id && editingCommentId === comment.id && (
                                <Button
                                  size="small"
                                  onClick={() => {
                                    setEditingCommentId(null);
                                    setEditingCommentContent('');
                                  }}
                                >
                                  Cancel
                                </Button>
                              )}
                              {comment.author.id === user?.id && (
                                <Button
                                  size="small"
                                  color="error"
                                  startIcon={<Delete />}
                                  disabled={deletingCommentId === comment.id}
                                  onClick={() => void handleDeleteComment(comment)}
                                >
                                  {deletingCommentId === comment.id ? 'Deleting...' : 'Delete'}
                                </Button>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </ListItem>
                      <Divider variant="inset" component="li" />
                    </React.Fragment>
                  ))
                )}
              </List>

              {/* Add Comment Input */}
              {isAuthenticated ? (
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Add a comment..."
                    value={newComment}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewComment(e.target.value)}
                  />
                  <Button variant="contained" onClick={handleAddComment} disabled={!newComment.trim()}>
                    Post
                  </Button>
                </Box>
              ) : (
                 <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                   Please log in to comment.
                 </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => toggleLikePost(selectedDiscussion.id)} startIcon={<ThumbUp />}>
                Like ({selectedDiscussion.likes})
              </Button>
              {selectedDiscussion.author.id === user?.id && (
                <Button onClick={() => startDiscussionEdit(selectedDiscussion)} startIcon={<Edit />}>
                  Edit
                </Button>
              )}
              {selectedDiscussion.author.id === user?.id && (
                <Button
                  color="error"
                  onClick={() => void handleDeleteDiscussion(selectedDiscussion)}
                  startIcon={<Delete />}
                  disabled={deletingDiscussionId === selectedDiscussion.id}
                >
                  {deletingDiscussionId === selectedDiscussion.id ? 'Deleting...' : 'Delete'}
                </Button>
              )}
              <Button onClick={() => handleShare(selectedDiscussion.title)} startIcon={<Share />}>
                Share
              </Button>
              <Button
                onClick={() =>
                  openReportDialog({
                    type: 'discussion',
                    id: selectedDiscussion.id,
                    title: selectedDiscussion.title,
                  })
                }
                startIcon={<FlagOutlined />}
              >
                Report
              </Button>
              <Button onClick={handleCloseViewDialog}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog open={reportDialogOpen} onClose={() => setReportDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Report Content</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" paragraph>
            {reportTarget ? `You are reporting: ${reportTarget.title}` : 'Select a post or comment to report.'}
          </Typography>
          <TextField
            select
            fullWidth
            margin="normal"
            label="Reason"
            value={reportReason}
            onChange={(e) => setReportReason(e.target.value)}
          >
            <MenuItem value="misinformation">Misinformation</MenuItem>
            <MenuItem value="unsafe_advice">Unsafe Advice</MenuItem>
            <MenuItem value="spam">Spam</MenuItem>
            <MenuItem value="abuse">Abuse</MenuItem>
            <MenuItem value="harassment">Harassment</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </TextField>
          <TextField
            fullWidth
            multiline
            rows={4}
            margin="normal"
            label="Details"
            value={reportDetails}
            onChange={(e) => setReportDetails(e.target.value)}
            placeholder="Explain what should be reviewed."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void handleSubmitReport()} disabled={reportSubmitting || !reportTarget}>
            {reportSubmitting ? 'Submitting...' : 'Submit Report'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={farmerDialogOpen} onClose={() => setFarmerDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Farmer Verification</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" paragraph>
            Verified farmers receive a trust badge across community posts and comments. This verification is only available if you have at least one farm record.
          </Typography>
          {farms.length === 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No farms found for your account. Create a farm first, then come back to request verification.
            </Alert>
          )}
          <TextField
            fullWidth
            multiline
            rows={4}
            margin="normal"
            label="Notes for Admin (optional)"
            value={farmerRequestNotes}
            onChange={(e) => setFarmerRequestNotes(e.target.value)}
            placeholder="Add any details that help admins verify your farm ownership and activity."
            disabled={myFarmerVerification?.verificationStatus === 'approved'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFarmerDialogOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => void handleSubmitFarmerVerification()}
            disabled={farmerSubmitting || farms.length === 0 || myFarmerVerification?.verificationStatus === 'approved'}
          >
            {myFarmerVerification?.verificationStatus === 'approved'
              ? 'Already Verified'
              : farmerSubmitting
                ? 'Submitting...'
                : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={expertDialogOpen} onClose={() => setExpertDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Expert Verification</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" paragraph>
            Provide a short profile so admins can verify your expertise and highlight you in the community.
          </Typography>
          <TextField
            fullWidth
            margin="normal"
            label="Professional Headline"
            value={expertHeadline}
            onChange={(e) => setExpertHeadline(e.target.value)}
            placeholder="Example: Agronomist focused on maize and irrigation systems"
            disabled={myExpertProfile?.verificationStatus === 'approved'}
          />
          <TextField
            fullWidth
            multiline
            rows={4}
            margin="normal"
            label="Bio"
            value={expertBio}
            onChange={(e) => setExpertBio(e.target.value)}
            placeholder="Describe your experience, qualifications, and the kind of guidance you provide."
            disabled={myExpertProfile?.verificationStatus === 'approved'}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Expertise Tags"
            value={expertTags}
            onChange={(e) => setExpertTags(e.target.value)}
            placeholder="Maize, Soil Health, Irrigation"
            disabled={myExpertProfile?.verificationStatus === 'approved'}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Counties Served"
            value={expertCounties}
            onChange={(e) => setExpertCounties(e.target.value)}
            placeholder="Nakuru, Uasin Gishu, Kiambu"
            disabled={myExpertProfile?.verificationStatus === 'approved'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExpertDialogOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => void handleSubmitExpertRequest()}
            disabled={expertSubmitting || myExpertProfile?.verificationStatus === 'approved'}
          >
            {myExpertProfile?.verificationStatus === 'approved'
              ? 'Already Verified'
              : expertSubmitting
                ? 'Saving...'
                : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={adminReviewDialogOpen}
        onClose={() => setAdminReviewDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Admin Review</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" paragraph>
            {adminReviewTarget
              ? `Action: ${adminReviewAction} on ${adminReviewTarget.targetTitle}`
              : 'Select a moderation item to review.'}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Review Notes"
            value={adminReviewNotes}
            onChange={(e) => setAdminReviewNotes(e.target.value)}
            placeholder="Add the reasoning for this moderation decision."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdminReviewDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => void handleSubmitAdminReview()}
            disabled={adminReviewSubmitting || !adminReviewTarget}
          >
            {adminReviewSubmitting ? 'Saving...' : 'Save Review'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={snackbarMessage}
        action={
          <Button color="secondary" size="small" onClick={handleCloseSnackbar}>
            CLOSE
          </Button>
        }
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CommunityHub;
